import React, { useState, ReactElement, useRef } from "react";
import { IChartApi, ISeriesApi } from "lightweight-charts";
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface RectangleToolProps {
  onRectChange?: (rect: any) => void;
  onSelectModeChange?: (selecting: boolean) => void;
  children: ReactElement<{ disableInteraction?: boolean }>;
  width?: number;
  height?: number;
  chartApi?: IChartApi | null;
  ohlcData?: any[];
  areaSeries?: any; // 传入主图的areaSeries
  hideControls?: boolean; // 新增：隐藏内部控制按钮
  externalSelectMode?: boolean; // 新增：外部控制的选择模式
}

// 暴露给外部的方法接口
export interface RectangleToolRef {
  clearArea: () => void;
  setAreaByIndex: (startIndex: number, endIndex: number) => void; // AI Agent 用来设置矩形
}

const RectangleTool = React.forwardRef<RectangleToolRef, RectangleToolProps>(({
  onRectChange,
  onSelectModeChange,
  children,
  width = 600,
  height = 400,
  chartApi,
  ohlcData = [],
  areaSeries,
  hideControls = false,
  externalSelectMode = false
}, ref) => {
  const { t } = useTranslation();
  const [selectMode, setSelectMode] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [end, setEnd] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [rectActive, setRectActive] = useState(false);
  const histogramSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);

  // 使用外部控制的选择模式或内部状态
  const currentSelectMode = hideControls ? externalSelectMode : selectMode;

  // 当外部状态变化时，同步内部状态
  React.useEffect(() => {
    if (hideControls && externalSelectMode !== selectMode) {
      setSelectMode(externalSelectMode);
      if (!externalSelectMode) {
        setDrawing(false);
        setStart(null);
        setEnd(null);
      }
    }
  }, [externalSelectMode, hideControls, selectMode]);

  // 像素x -> K线time
  const getBarTimeFromMouseX = (x: number) => {
    if (!chartApi) return null;
    const t = chartApi.timeScale().coordinateToTime(x);
    if (typeof t === 'number') return t;
    if (typeof t === 'string') return Number(t);
    return null;
  };

  // 像素y -> 价格
  const getPriceFromMouseY = (y: number) => {
    if (!areaSeries) return null;
    const price = areaSeries.coordinateToPrice(y);
    return price ?? null;
  };

  // K线time -> 最近K线索引
  const getNearestBarIndex = (time: number | null) => {
    if (!ohlcData.length || time == null) return 0;
    let nearest = 0;
    let minDiff = Infinity;
    for (let i = 0; i < ohlcData.length; i++) {
      const diff = Math.abs(ohlcData[i].time - time);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = i;
      }
    }
    return nearest;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!currentSelectMode || !chartApi) return;
    setDrawing(true);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setEnd(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drawing || !chartApi) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseUp = () => {
    if (!currentSelectMode || !drawing || !start || !end) return;

    // No data or missing chart objects means we cannot draw a valid rectangle
    if (!ohlcData.length || !chartApi || !areaSeries) {
      setDrawing(false);
      setStart(null);
      setEnd(null);
      return;
    }

    setDrawing(false);

    // 1. 获取K线时间和价格
    const startTime = getBarTimeFromMouseX(start.x);
    const endTime = getBarTimeFromMouseX(end.x);
    const startPrice = getPriceFromMouseY(start.y);
    const endPrice = getPriceFromMouseY(end.y);

    // 2. 找到最近的K线索引
    const startIndex = getNearestBarIndex(startTime);
    const endIndex = getNearestBarIndex(endTime);

    // 3. 生成区间数据 - 确保选中完整的时间区域
    const left = Math.min(startIndex, endIndex);
    const right = Math.max(startIndex, endIndex);
    
    // 获取选中区域内的所有K线数据
    const selectedData = ohlcData.slice(left, right + 1);
    if (!selectedData.length) {
      // Nothing selected; keep tool state but avoid corrupting the chart
      setStart(null);
      setEnd(null);
      setRectActive(false);
      return;
    }
    
    // 计算价格范围
    const prices = selectedData
      .map(item => [item.high, item.low])
      .flat()
      .filter((v) => Number.isFinite(v));
    if (!prices.length) {
      setStart(null);
      setEnd(null);
      setRectActive(false);
      return;
    }
    const top = Math.max(...prices);
    const bottom = Math.min(...prices);
    // logger.log(bottom, top);
    // 4. 生成 histogramSeries 数据 - 覆盖整个选中区域
    if (chartApi) {
      // 如果之前有高亮，先移除
      if (histogramSeriesRef.current) {
        chartApi.removeSeries(histogramSeriesRef.current);
        histogramSeriesRef.current = null;
      }
      // 创建新的 histogramSeries
      const histSeries = chartApi.addHistogramSeries({
        color: "rgba(255,0,0,0.2)",
        priceLineVisible: false,
        base: bottom
      });
      const bandData = [];
      for (let i = left; i <= right; i++) {
        bandData.push({
          time: ohlcData[i].time,
          value: top,
          color: "rgba(255,0,0,0.2)",
          // base: bottom // base 只在 series 级别设置
        });
      }
      histSeries.setData(bandData);
      histogramSeriesRef.current = histSeries;
      setRectActive(true);
    }
    if (onRectChange) {
      // 计算像素坐标（start/end 相对于容器左上角）
      onRectChange({
        startIndex: left,
        endIndex: right,
        startTime: ohlcData[left]?.time,
        endTime: ohlcData[right]?.time,
        startPrice: bottom,
        endPrice: top,
        selectedData: selectedData, // 传递选中的数据
        pixelStart: start,
        pixelEnd: end
      });
      // logger.log("start", startTime);
      // logger.log("end", endTime);
    }
  };

  // 程序化设置矩形（AI Agent 用）
  const setAreaByIndex = (startIndex: number, endIndex: number) => {
    if (!ohlcData.length || !chartApi || !areaSeries) return;
    const left = Math.max(0, Math.min(startIndex, endIndex));
    const right = Math.min(ohlcData.length - 1, Math.max(startIndex, endIndex));
    if (right < left) return;

    const selectedData = ohlcData.slice(left, right + 1);
    if (!selectedData.length) return;

    const prices = selectedData
      .map(item => [item.high, item.low])
      .flat()
      .filter((v) => Number.isFinite(v));
    if (!prices.length) return;

    const top = Math.max(...prices);
    const bottom = Math.min(...prices);

    // draw histogram band
    if (histogramSeriesRef.current) {
      chartApi.removeSeries(histogramSeriesRef.current);
      histogramSeriesRef.current = null;
    }
    const histSeries = chartApi.addHistogramSeries({
      color: "rgba(255,0,0,0.2)",
      priceLineVisible: false,
      base: bottom
    });
    const bandData = [];
    for (let i = left; i <= right; i++) {
      bandData.push({
        time: ohlcData[i].time,
        value: top,
        color: "rgba(255,0,0,0.2)",
      });
    }
    histSeries.setData(bandData);
    histogramSeriesRef.current = histSeries;
    setRectActive(true);

    onRectChange?.({
      startIndex: left,
      endIndex: right,
      startTime: ohlcData[left]?.time,
      endTime: ohlcData[right]?.time,
      startPrice: bottom,
      endPrice: top,
      selectedData: selectedData,
    });
  };

  // 清除高亮
  const clearArea = () => {
    if (histogramSeriesRef.current && chartApi) {
      chartApi.removeSeries(histogramSeriesRef.current);
      histogramSeriesRef.current = null;
    }
    setStart(null);
    setEnd(null);
    setRectActive(false);
    // 通知外部组件矩形已被清除
    if (onRectChange) onRectChange(null);
  };

  const chartWithProps = children;

  React.useImperativeHandle(ref, () => ({
    clearArea: clearArea,
    setAreaByIndex: setAreaByIndex,
  }));

  return (
    <div style={{ position: "relative", width, height }}>
      {/* 工具按钮 */}
      {!hideControls && (
        <div style={{ marginBottom: 8 }}>
          <button
            style={{
              background: currentSelectMode ? "#d0f0d0" : "#f0f0f0",
              border: "1px solid #888",
              borderRadius: 6,
              padding: "6px 16px",
              cursor: "pointer"
            }}
            onClick={() => {
              setSelectMode(m => {
                const next = !m;
                if (!next) {
                  setDrawing(false);
                  setStart(null);
                  setEnd(null);
                }
                return next;
              });
              onSelectModeChange?.(!currentSelectMode);
            }}
          >
            {currentSelectMode ? t('tools.exitRectangleTool') : t('tools.rectangleTool')}
          </button>
          {rectActive && (
            <button
              style={{
                background: "#f0f0f0",
                border: "1px solid #888",
                borderRadius: 6,
                padding: "6px 16px",
                cursor: "pointer",
                marginLeft: 8
              }}
              onClick={() => {
                clearArea();
                if (onRectChange) onRectChange(null);
              }}
            >
              {t('tools.removeRectangle')}
            </button>
          )}
        </div>
      )}

      {/* 图表容器 */}
      <div
        ref={containerRef}
        style={{
          width,
          height,
          position: "relative",
          border: "none",
          cursor: currentSelectMode ? "crosshair" : "default"
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {/* 子组件（Chart）加 pointerEvents 控制 */}
        <div style={currentSelectMode ? { 
          pointerEvents: 'none',
          userSelect: 'none'
        } : {}}>
          {chartWithProps}
        </div>
        {/* 绘制中预览 */}
        {drawing && start && end && (
          <div
            style={{
              position: "absolute",
              left: Math.min(start.x, end.x),
              width: Math.abs(end.x - start.x),
              top: Math.min(start.y, end.y),
              height: Math.abs(end.y - start.y),
              background: "rgba(255,0,0,0.2)",
              border: "2px solid #f00",
              pointerEvents: "none",
              zIndex: 2
            }}
          />
        )}
        {/* 工具激活时的遮罩提示 */}
        {currentSelectMode && !drawing && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.02)",
              pointerEvents: "none",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "#666"
            }}
          >
          </div>
        )}
      </div>
    </div>
  );
});

export default RectangleTool; 
